import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { useState } from 'react';
import { getLmsRecord, sendChatFeedback } from '../api/backend';
import useDemo from '../hooks/useDemo';

// Prevent markdown links inside the answer from duplicating the source list.
function PlainTextLink({ children }) {
  return <span>{children}</span>;
}

// Expandable LMS source card: clicking never leaves the chat — the record
// is fetched from the backend and rendered in place (the Moodle URL itself
// would bounce the viewer to the LMS login screen).
function LmsSourceCard({ source }) {
  const { token: demoToken } = useDemo();
  const [expanded, setExpanded] = useState(false);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRecord = async () => {
    if (record || error || loading || !source.url) return;
    setLoading(true);
    try {
      setRecord(await getLmsRecord(source.url, demoToken));
    } catch {
      setError('Запись LMS сейчас недоступна. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) fetchRecord();
  };

  const dueDate = record?.due_date
    ? new Date(record.due_date).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <div className="flex flex-col gap-1 rounded-ai border border-ai-teal-light bg-ai-teal-light/40 px-3 py-2 text-sm text-ai-teal">
      <button
        type="button"
        onClick={handleToggle}
        disabled={!source.url}
        title={source.url ? 'Показать запись из LMS' : undefined}
        className="flex items-center gap-2 text-left"
      >
        <span>🎓</span>
        <span className="font-medium line-clamp-1">{source.title}</span>
        {source.url && (
          <span
            className="ml-auto text-xs opacity-80"
            style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          >
            ▼
          </span>
        )}
      </button>
      {(source.module || source.section) && (
        <div className="flex flex-wrap gap-1 text-xs opacity-80">
          {source.module && (
            <span className="rounded-full bg-white/60 px-2 py-0.5">{source.module}</span>
          )}
          {source.section && (
            <span className="rounded-full bg-white/60 px-2 py-0.5">{source.section}</span>
          )}
        </div>
      )}
      {expanded && (
        <div className="mt-1 rounded-ai bg-white/60 px-3 py-2">
          {loading && (
            <p className="text-xs text-ai-text-muted">Загружаю запись из LMS…</p>
          )}
          {!loading && error && (
            <p className="text-xs text-ai-text-muted">{error}</p>
          )}
          {!loading && record && (
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ai-text-muted">
                {record.course && (
                  <span>
                    Курс: <span className="font-medium text-ai-text">{record.course}</span>
                  </span>
                )}
                {record.section && (
                  <span>
                    Раздел: <span className="font-medium text-ai-text">{record.section}</span>
                  </span>
                )}
                {record.module_type && (
                  <span>
                    Тип: <span className="font-medium text-ai-text">{record.module_type}</span>
                  </span>
                )}
                {dueDate && (
                  <span>
                    Дедлайн: <span className="font-medium text-ai-text">{dueDate}</span>
                  </span>
                )}
              </div>
              {record.description && (
                <p className="whitespace-pre-wrap text-xs leading-relaxed text-ai-text">
                  {record.description}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KbSourceCard({ source }) {
  const [expanded, setExpanded] = useState(false);
  const details = source.document_type;

  return (
    <div className="flex flex-col gap-1 rounded-ai border border-ai-primary-light bg-ai-primary-light/40 px-3 py-2 text-sm text-ai-primary">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        disabled={!source.citation}
        title={source.citation ? 'Показать цитату из документа' : undefined}
        className="flex items-center gap-2 text-left"
      >
        <span>📚</span>
        <span className="font-medium line-clamp-1">{source.title}</span>
        {source.citation && (
          <span
            className="ml-auto text-xs opacity-80"
            style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          >
            ▼
          </span>
        )}
      </button>
      {details && (
        <div className="flex flex-wrap gap-1 text-xs opacity-80">
          {source.document_type && (
            <span className="rounded-full bg-white/60 px-2 py-0.5 capitalize">
              {source.document_type}
            </span>
          )}
        </div>
      )}
      {expanded && source.citation && (
        <div className="mt-1 rounded-ai bg-white/60 px-3 py-2">
          <p className="whitespace-pre-wrap text-xs leading-relaxed text-ai-text">
            {source.citation}
          </p>
          {source.url && (
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-xs font-medium underline"
            >
              Открыть оригинал ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function SourceCard({ source }) {
  const isKb = source.type === 'kb';
  const isLms = source.type === 'lms';
  const details = source.document_type || source.module || source.section;

  if (isKb) {
    return <KbSourceCard source={source} />;
  }

  if (isLms) {
    return <LmsSourceCard source={source} />;
  }

  return (
    <a
      href={source.url || '#'}
      target="_blank"
      rel="noreferrer"
      className="flex flex-col gap-1 rounded-ai border border-ai-teal-light bg-ai-teal-light/40 px-3 py-2 text-sm text-ai-teal transition hover:shadow-ai"
    >
      <div className="flex items-center gap-2">
        <span>🎓</span>
        <span className="font-medium line-clamp-1">{source.title}</span>
      </div>
      {details && (
        <div className="flex flex-wrap gap-1 text-xs opacity-80">
          {source.module && (
            <span className="rounded-full bg-white/60 px-2 py-0.5">
              {source.module}
            </span>
          )}
          {source.section && (
            <span className="rounded-full bg-white/60 px-2 py-0.5">
              {source.section}
            </span>
          )}
        </div>
      )}
    </a>
  );
}

function StarRating({ initialScore, logId }) {
  const [score, setScore] = useState(initialScore || 0);
  const [hover, setHover] = useState(0);
  const [submitted, setSubmitted] = useState(Boolean(initialScore));
  const [error, setError] = useState(null);

  const handleRate = async (value) => {
    if (!logId) return;
    setScore(value);
    setSubmitted(true);
    setError(null);
    try {
      await sendChatFeedback(logId, value);
    } catch (err) {
      setError('Не удалось сохранить оценку');
      setSubmitted(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-xs text-ai-text-muted">
          {submitted ? 'Спасибо за оценку!' : 'Оцените ответ:'}
        </span>
        <div className="flex">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
            <button
              key={value}
              type="button"
              aria-label={`Оценить ответ на ${value} из 10`}
              disabled={submitted}
              className={`px-0.5 py-0 text-lg leading-none transition ${
                submitted ? 'cursor-default' : 'cursor-pointer hover:scale-110'
              } ${
                value <= (hover || score)
                  ? 'text-ai-warning'
                  : 'text-ai-text-muted/40'
              }`}
              style={{ color: value <= (hover || score) ? 'var(--ai-warning)' : 'var(--ai-text-muted)' }}
              onMouseEnter={() => !submitted && setHover(value)}
              onMouseLeave={() => setHover(0)}
              onClick={() => handleRate(value)}
            >
              ★
            </button>
          ))}
        </div>
      </div>
      {error && <p className="text-xs text-ai-error">{error}</p>}
    </div>
  );
}

function Message({ message }) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[90%] rounded-ai px-5 py-4 sm:max-w-[80%] ${
          isUser
            ? 'bg-ai-primary text-white'
            : 'ai-card'
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
        ) : (
          <div className="markdown-content text-sm leading-relaxed text-ai-text">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              skipHtml
              components={{
                a: PlainTextLink,
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}

        {!isUser && message.sources && message.sources.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {message.sources.map((source, index) => (
              <SourceCard key={index} source={source} />
            ))}
          </div>
        )}

        {!isUser && message.logId && (
          <StarRating initialScore={message.feedbackScore} logId={message.logId} />
        )}

        {!isUser && message.meta && (
          <p className="mt-3 text-xs text-ai-text-muted">{message.meta}</p>
        )}
      </div>
    </div>
  );
}

export default Message;
